<template>
  <div class="performance-widget-container">
    <widget-header>{{ config.name || 'Performance by Category' }}</widget-header>
    <div class="widget-container">
      <div class="content">
        <div class="category-main">
          <div v-for="cat in tallies" :key="cat.id" class="category-wrapper">
            <img
              v-if="categoryImages[cat.id]"
              :src="categoryImages[cat.id]"
              class="category-image"
              :alt="`${cat.name} icon`"
            />
            <div class="progress-wrapper">
              <span class="category-row" :data-test="`progress-bar-cat-${cat.id}`">
                <span class="category-name" data-test="category-name">
                  {{ cat.name }}
                </span>
                <span class="category-percent-correct" data-test="category-percent-correct">
                  {{ cat.percentCorrect }}%
                </span>
              </span>
              <widget-progress-bar
                :count="cat.count"
                :red="cat.red"
                :yellow="cat.yellow"
                :green="cat.green"
              ></widget-progress-bar>
            </div>
          </div>
        </div>
        <div class="button-wrapper">
          <div class="button-link">
            <router-link :to="{ name: 'progress', query: queryString }" class="router-link" data-test="view-all">
              View All
            </router-link>
          </div>
        </div>
      </div>
      <div class="obfuscator" :class="{ revealed: hasPaidProgress }">
        <div class="obfuscator-content">
          <div class="icon-box">
            <icon name="lock-uui" variant="364152"></icon>
          </div>
          <h3>Subscribe to Unlock</h3>
          <p>Performance by category requires a subscription.</p>
          <button
            class="subscribe-button"
            @click="showUpSellModal"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WidgetProgressBar from './WidgetProgressBar';
import Icon from '@components/singletons/icon';
import WidgetHeader from '@components/dashboard-widgets/WidgetHeader';
import PerformanceMixin from '@/mixins/performance';
import CategoriesMixin from '@/mixins/categories';
import { PAID_FEATURES } from '@/utils/acl';

export default {
  components: { WidgetHeader, WidgetProgressBar, Icon },
  mixins: [PerformanceMixin, CategoriesMixin],
  computed: {
    accessibleCategories() {
      return this.categoryIds.map(id => this.allCategories[id])
        .filter(category => this.$access(this.$store, 'category', category.id));
    },
    allCategories() {
      return this.$store.getters['categories/categories'];
    },
    categoryImages() {
      const images = {};
      for (const cat of this.tallies) {
        images[cat.id] = this.categoryImage(this.allCategories[cat.id]);
      }
      return images;
    },
    imageAttachmentIds() {
      return this.tallies.map(c => this.allCategories[c.id].imageAttachmentId).filter(id => id);
    },
    hasPaidProgress() {
      return this.$access(this.$store, 'feature', PAID_FEATURES.PROGRESS);
    },
    parentCategoryIds() {
      return this.accessibleCategories.filter(category => !category.parentCategoryId).map(category => category.id);
    },
    queryString() {
      const query = { 'categories-only': 'true' };
      if (this.resourceType == 'categoryType') {
        query.categoryTypeId = this.resourceId;
      } else if (this.resourceType == 'grouping') {
        query.groupingId = this.resourceId;
      }
      return query;
    },
    tallies() {
      const totals = [];
      const greenFlashcardStatus = this.$store.getters['flashcardStatuses/greenFlashcardStatus'];
      const redFlashcardStatus = this.$store.getters['flashcardStatuses/redFlashcardStatus'];
      const yellowFlashcardStatus = this.$store.getters['flashcardStatuses/yellowFlashcardStatus'];

      for (let id of this.parentCategoryIds) {
        const data = {};
        const category = this.allCategories[id];
        const ids = [id, ...category.descendants];
        const leafs = this.$store.getters['categories/all'].filter(c => ids.includes(c.id) && c.children.length < 1);
        const states = this.flashcardStateList.filter(s => leafs.map(c => c.id).includes(s.categoryId));

        data['count'] = leafs.map(c => c.counts.total).reduce((a, b) => a + b, 0);
        data['correct'] = states.filter(s => s.correct && [greenFlashcardStatus.id, redFlashcardStatus.id, yellowFlashcardStatus.id].includes(s.flashcardStatusId)).length;
        data['green'] = states.filter(s => s.flashcardStatusId == greenFlashcardStatus.id).length;
        data['yellow'] = states.filter(s => s.flashcardStatusId == yellowFlashcardStatus.id).length;
        data['red'] = states.filter(s => s.flashcardStatusId == redFlashcardStatus.id).length;
        data['answered'] = data['green'] + data['yellow'] + data['red'];
        data['id'] = category.id;
        data['name'] = category.name;
        data['percentCorrect'] = Math.round(data['correct'] / data['answered'] * 100) || 0;

        totals.push(data);
      }
      return totals
        .filter(cat => cat.count > 0)
        .sort((a, b) => b.percentCorrect - a.percentCorrect)
        .slice(0, 4);
    },
  },
  watch: {
    imageAttachmentIds() {
      this.fetchCategoryImages();
    },
  },
  mounted() {
    this.fetchCategoryImages();
  },
  methods: {
    fetchCategoryImages() {
      if (this.imageAttachmentIds.length > 0) {
        this.$store.dispatch('attachments/fetch', { ids: this.imageAttachmentIds });
      }
    },
    categoryImage(category) {
      const image = this.$store.getters['attachments/get'][category.imageAttachmentId];
      return image ? image.url : '';
    },
    showUpSellModal() {
      const params = {
        show: true,
        source: {
          type: 'Locked Progress Modal',
        },
        resource: {
          type: 'progress',
          id: 'navigationID',
        },
      };
      this.$store.dispatch('ui/toggleUpsellModal', params);
    },
  },
};
</script>

<style scoped lang="scss">
.performance-widget-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  max-width: 100%;
}

.widget-container {
  background: #FDFDFD;
  border: 1px solid var(--Colors-Border-border-secondary);
  border-radius: rem-calc(16);
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  .content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
    min-width: 0;
    min-height: 0;
  }

  .button-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 12px 16px;
    box-sizing: border-box;
    min-height: rem-calc(60);
    min-width: 0;
    width: 100%;
    flex-shrink: 0;
    background: #FDFDFD;
  }

  .button-link {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    height: rem-calc(40);
    min-height: rem-calc(40);
    max-height: rem-calc(40);
    box-sizing: border-box;
    padding: 0 rem-calc(12);
    background-color: #fff;
    border: 1px solid #cdd5df;
    border-radius: rem-calc(8);
    box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);

    .router-link {
      display: inline-flex;
      align-items: center;
      color: #364152;
      font-size: rem-calc(14);
      font-weight: 600;
      line-height: rem-calc(20);
      text-decoration: none;
    }
  }

  /* Match Questions Answered `.chart-wrapper`: rounded panel so the white → footer seam follows the curve. */
  .category-main {
    background: var(--Colors-Foreground-fg-white);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 0;
    padding: rem-calc(29) 16px rem-calc(12) 16px;
    flex: 1;
    min-width: 0;
    min-height: 0;
    border-radius: rem-calc(16);
    border-bottom: 1px solid var(--Colors-Border-border-secondary);
    overflow: hidden;
  }

  .obfuscator {
    padding: 1.5rem;
    display: grid;
    justify-content: center;
    align-items: center;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
    background-color: rgba(255, 255, 255, 0.4);
    backdrop-filter: blur(4px);

    &.revealed {
      display: none;
    }

    .obfuscator-content {
      padding: 6rem 2rem;
      text-align: center;
      z-index: 1;
      position: relative;

      ::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        background-image: url('../../assets/images/dot-grid.svg');
        background-size: cover;
        -webkit-mask:radial-gradient(circle at 50% 50%, white, transparent 65%);
          mask:radial-gradient(circle at 50% 50%, white, transparent 65%);
        z-index: -1;
      }

      .icon-box {
        width: 2.25rem;
        height: 2.25rem;
        margin: 0 auto;
        display: grid;
        place-content: center;
        background-color: #fff;
        border: 1px solid #CDD5DF;
        border-radius: 0.5rem;
      }

      h3 {
        font-size: rem-calc(20);
        font-weight: 600;
      }

      p {
        font-size: rem-calc(16);
        font-weight: 400;
      }

      .subscribe-button {
        background-color: #326FF0;
        padding: 0.75rem 1.25rem;
        border-radius: .5rem;
        font-size: rem-calc(16);
        font-weight: 600;
        color: #fff;
      }
    }
  }
}

.category-image {
  flex-shrink: 0;
  width: rem-calc(40);
  height: rem-calc(40);
  object-fit: contain;
}

.category-name {
  font-size: rem-calc(14);
  font-weight: 500;
  line-height: rem-calc(20);
  color: var(--text-primary-900);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 0;
  min-width: 0;
}

.category-wrapper {
  display: flex;
  align-items: center;
  gap: rem-calc(16);
  min-height: rem-calc(56);
  padding-bottom: rem-calc(16);
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;

  &:not(:first-child) {
    padding-top: rem-calc(16);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-secondary);
  }

  .progress-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    gap: rem-calc(8);
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }
}

.category-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: rem-calc(8);
  min-width: 0;
  width: 100%;
  min-height: rem-calc(20);
}

.category-percent-correct {
  flex-shrink: 0;
  white-space: nowrap;
  font-size: rem-calc(14);
  font-weight: 500;
  line-height: rem-calc(20);
  color: var(--text-tertiary);
}
</style>
